import type { ClassDefinition } from "@hd2/data";
import {
	Combobox,
	ComboboxInput,
	ComboboxOption,
	ComboboxOptions,
} from "@headlessui/react";
import { useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { useClasses } from "../hooks/use-classes.ts";

interface ClassSearchProps {
	expand?: boolean;
}

export function ClassSearch({ expand = false }: ClassSearchProps) {
	const navigate = useNavigate();
	const { classes, loading } = useClasses();
	const [query, setQuery] = useState("");

	const normalized = query.trim().toLowerCase();
	const filtered = normalized
		? classes.filter(
				(c) =>
					c.name.toLowerCase().includes(normalized) ||
					c.role.toLowerCase().includes(normalized) ||
					c.tags.some((t) => t.toLowerCase().includes(normalized)),
			)
		: classes;

	const onPick = (cls: ClassDefinition | null) => {
		if (!cls) {
			return;
		}
		setQuery("");
		navigate({ to: "/classes/$slug", params: { slug: cls.slug } });
	};

	return (
		<Combobox<ClassDefinition | null>
			immediate
			value={null}
			onChange={onPick}
			onClose={() => setQuery("")}
		>
			<div className="relative">
				<span className="material-symbols-outlined pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-sm text-outline">
					search
				</span>
				<ComboboxInput
					aria-label="Search classes"
					autoComplete="off"
					placeholder={loading ? "LOADING CLASSES…" : "SEARCH CLASSES…"}
					onChange={(e) => setQuery(e.target.value)}
					className={`${expand ? "w-full" : "w-56"} border-0 border-b-2 border-outline bg-surface-container-lowest py-1 pr-2 pl-8 font-mono text-xs tracking-wider text-on-surface uppercase placeholder:text-outline-variant focus:border-primary focus:outline-none`}
				/>
			</div>
			<ComboboxOptions
				anchor={{ to: "bottom end", gap: 4 }}
				className="z-50 w-72 border-l-4 border-primary bg-surface-container-low shadow-[4px_4px_0_0_rgba(0,0,0,1)] empty:hidden"
			>
				{filtered.length === 0 && !loading && (
					<div className="px-4 py-3 font-mono text-[10px] text-outline uppercase">
						{"NO MATCHES // ADJUST QUERY"}
					</div>
				)}
				{filtered.map((c) => (
					<ComboboxOption
						key={c.slug}
						value={c}
						className="flex cursor-pointer items-center justify-between gap-3 px-4 py-2 font-mono text-xs text-on-surface-variant uppercase data-focus:bg-surface-container-highest data-focus:text-primary"
					>
						<span className="font-headline font-bold tracking-tight">
							{c.name}
						</span>
						<span className="text-[9px] text-outline">{c.role}</span>
					</ComboboxOption>
				))}
			</ComboboxOptions>
		</Combobox>
	);
}
